import React from "react";
import { Sliders, Activity, Music, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const scenarios = [{
  id: "mixing",
  title: "Professional Mixing",
  subtitle: "Sonic Clarity & Depth",
  description: "Transform raw tracks into a cohesive, radio-ready masterpiece. Our mixing process focuses on frequency balance, spatial positioning, and dynamic control, ensuring every element of your production breathes with intentionality and power. We utilize high-end analog emulation and surgical digital precision.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689780/img/studio_mixing_console.png",
  imageAlt: "Close up of a high-end mixing console with faders and glowing lights in a dark studio",
  proTipTitle: "Technical Spec",
  proTipText: "Deliver tracks as 24-bit WAV files with at least -6dB of headroom. Remove all master bus processing before export for optimal results.",
  icon: Sliders,
  reverse: false
}, {
  id: "mastering",
  title: "Precision Mastering",
  subtitle: "The Final Polish",
  description: "The definitive step in the production chain. We optimize your mix for all playback systems, from club stacks to smartphone speakers. Our mastering suite provides the necessary loudness, stereo width, and tonal consistency to compete on global streaming platforms while preserving the soul of the performance.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689780/img/mastering_waveform_display.png",
  imageAlt: "A digital waveform displayed on a high-resolution monitor in a dark mastering suite",
  proTipTitle: "Technical Spec",
  proTipText: "Our mastering chain includes linear phase EQ and multiband compression to ensure phase coherence across the entire frequency spectrum.",
  icon: Activity,
  reverse: true
}, {
  id: "licensing",
  title: "Beat Licensing",
  subtitle: "Exclusive Sonic Assets",
  description: "Access a curated library of premium instrumentals across hip-hop, R&B, and electronic genres. Our licensing tiers are designed to support independent artists and major labels alike, providing high-quality stems and clear usage rights to fuel your next creative breakthrough without legal friction.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689780/img/vinyl_record_texture.png",
  imageAlt: "Macro shot of vinyl record grooves reflecting light in a monochromatic setting",
  proTipTitle: "Technical Spec",
  proTipText: "All licenses include high-quality WAV stems. Exclusive rights are available upon request for major commercial placements.",
  icon: Music,
  reverse: false
}];
export default function ProductionTiers() {
  return <section data-section-id="241041" id="production-tiers" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {}
        <div className="max-w-3xl mx-auto text-center mb-20 lg:mb-32">
          <div className="inline-flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-default font-medium tracking-widest uppercase text-muted-foreground">
              Service Packages
            </span>
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-default font-bold tracking-tighter mb-6 uppercase">
            Production Tiers
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-mono max-w-2xl mx-auto">
            Precision-engineered audio services for the modern creator. From initial tracking to the final master, we define the rhythm and soul of your sound.
          </p>
        </div>

        {}
        <div className="flex flex-col gap-24 lg:gap-40">
          {scenarios.map((scenario, i) => {
          const Icon = scenario.icon;
          return <div key={scenario.id} data-index={i} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                {}
                <div className={`order-1 ${scenario.reverse ? "lg:order-2" : "lg:order-1"}`}>
                  <div className="relative aspect-[4/5] w-full overflow-hidden rounded-md shadow-2xl bg-muted/10 border border-border/20">
                    <div className="absolute inset-0 bg-gradient-to-tr from-background/40 to-transparent z-10 mix-blend-overlay" />
                    <img src={scenario.image} alt={scenario.imageAlt} className="w-full h-full object-cover object-center transition-transform duration-700 grayscale hover:grayscale-0 hover:scale-105" loading="lazy" />
                    <div className="absolute bottom-4 right-4 z-20">
                      <span className="text-8xl font-default font-bold opacity-10 text-foreground select-none">
                        0{i + 1}
                      </span>
                    </div>
                  </div>
                </div>

                {}
                <div className={`order-2 flex flex-col justify-center ${scenario.reverse ? "lg:order-1" : "lg:order-2"}`}>
                  <div className="mb-8">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-md bg-primary/5 text-primary border border-primary/10 mb-6">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-3xl md:text-5xl font-default font-bold mb-3 tracking-tight uppercase">
                      {scenario.title}
                    </h3>
                    <p className="text-xl text-primary/60 font-mono mb-6">
                      
                    </p>
                    <p className="text-muted-foreground text-lg leading-loose font-default">
                      {scenario.description}
                    </p>
                  </div>

                  {}
                  <Card className="bg-white/5 backdrop-blur-md border-white/10 shadow-2xl rounded-md">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-xs font-mono tracking-widest uppercase text-primary flex items-center gap-2">
                        <span className="w-8 h-[1px] bg-primary/50 inline-block" />
                        Technical Spec
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <h4 className="text-lg font-default font-semibold mb-2 text-foreground">
                        {scenario.proTipTitle}
                      </h4>
                      <p className="text-muted-foreground leading-relaxed font-mono text-sm md:text-base">
                        {scenario.proTipText}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}